import React, { useEffect, useState, useCallback } from 'react';
import CommentInput from '../../custom/inputs/CommentInput';
import { useAuthInfo } from '../../../lib/jwt';
import { CommentDtoType } from '../../../types/dto/CommentDtoType';
import { EntityType } from '../../../types/dto/EntityType';
import { CommentListItem } from './CommentListItem';
import { CommentsDataProvider } from './CommentsDataProvider';

type CommentListProps = {
  className?: string;
  itemClassName?: string;
  type: EntityType;
  linkedId: number;
  sortType?: 'top' | 'newest';
  onCommentAdd: () => void;
  onCommentDelete: () => void;
};

const sortCommentList = (sortType: 'top' | 'newest', comments: CommentDtoType[]) => {
  switch (sortType) {
    case 'newest':
      return comments.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    case 'top':
      return comments.sort((a, b) => b.reactionsCount - a.reactionsCount);
    default:
      return comments;
  }
};

export function CommentList({
  className,
  itemClassName,
  type,
  linkedId,
  sortType,
  onCommentAdd,
  onCommentDelete
}: CommentListProps) {
  const [comments, setComments] = useState<CommentDtoType[]>();
  const [idToReply, setIdToReply] = useState<number>();
  const [authInfo] = useAuthInfo(false);
  const commetsPerRow = 3;
  const [showingComments, setShowingComments] = useState(1);

  const commentChangedHandler = useCallback(
    (changedItem: CommentDtoType) => {
      setComments(comments.map(t => (t.id === changedItem.id ? changedItem : t)));
    },
    [comments]
  );

  const commentDeletedHandler = useCallback(
    (deletedItem: CommentDtoType) => {
      setComments(comments.filter(t => !(t.id === deletedItem.id)));
      onCommentDelete();
    },
    [comments]
  );

  const commentAddedHandler = useCallback(
    (newItem: CommentDtoType) => {
      if (newItem.parentId === 0 || newItem.parentId === null) {
        setComments([...comments, newItem]);
      } else {
        setComments(
          comments.map(comment => {
            if (comment.id === newItem.parentId) {
              const updatedComment = comment;
              updatedComment.repliesCount += 1;
              return updatedComment;
            }
            return comment;
          })
        );
      }
      onCommentAdd();
    },
    [comments]
  );

  const handleMore = () => {
    setShowingComments(showingComments + commetsPerRow);
  };

  const onReply = useCallback((commentId: number) => {
    setIdToReply(commentId);
  }, []);

  return (
    <CommentsDataProvider
      type={type}
      linkedId={linkedId}
      userId={authInfo?.id}
      onLoaded={setComments}
    >
      <div className={`${className} flex flex-col text-white text-sm`}>
        <div className="max-h-96 overflow-y-scroll comment-section">
          {comments &&
            sortCommentList(sortType, comments)
              ?.slice(0, showingComments)
              ?.map(comment => (
                <CommentListItem
                  itemClassName={itemClassName}
                  key={comment.id}
                  type={type}
                  comment={comment}
                  replyId={idToReply}
                  linkedId={linkedId}
                  onReply={onReply}
                  onAdded={commentAddedHandler}
                  onChanged={commentChangedHandler}
                  onDeleted={commentDeletedHandler}
                />
              ))}

          {showingComments < comments?.length && (
            <div
              onClick={handleMore}
              className="px-4 py-4 text-center flex justify-center cursor-pointer"
            >
              <p className="showmore-comment">Show more comments</p>
            </div>
          )}
        </div>
        <CommentInput
          className={`mx-4 mb-4 mt-4 rounded-b-lg`}
          linkedId={linkedId}
          type={type}
          onSubmitted={commentAddedHandler}
        />
      </div>
    </CommentsDataProvider>
  );
}
